import { type ChangeEvent, type FormEvent, useEffect, useRef, useState } from "react";
import Icon from "./icons";

type RequestType = "contact" | "demo";

type FormData = {
  requestType: RequestType;
  name: string;
  email: string;
  company: string;
  phone: string;
  projectType: string;
  budget: string;
  message: string;
  website: string;
};

type SubmitState = "idle" | "sending" | "sent" | "error";

export type ContactPrefill = Partial<Pick<FormData, "projectType" | "budget" | "message">>;

type ContactSectionProps = {
  prefill?: ContactPrefill;
};

const initialForm: FormData = {
  requestType: "contact",
  name: "",
  email: "",
  company: "",
  phone: "",
  projectType: "",
  budget: "",
  message: "",
  website: "",
};

const servicePrompts: Record<string, string> = {
  "Web development": "Share your website address and the pages, updates, or fixes you need.",
  "Mobile development": "Tell us about your app, its platform (iOS or Android), and what needs to change.",
  "Software engineering": "Describe the software, feature, integration, or issue you need help with.",
  "Technical consulting": "What technical decision or challenge would you like help with?",
};

const encodeFile = (file: File): Promise<{ filename: string; content: string }> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve({ filename: file.name, content: String(reader.result).split(",")[1] });
  reader.onerror = () => reject(new Error(`Could not read ${file.name}. Please select it again.`));
  reader.readAsDataURL(file);
});

const ContactSection = ({ prefill }: ContactSectionProps) => {
  const [form, setForm] = useState<FormData>(() => ({ ...initialForm, ...prefill }));
  const [status, setStatus] = useState<SubmitState>("idle");
  const [feedback, setFeedback] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);
  const servicePrompt = servicePrompts[form.projectType];

  const handleFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = [...files, ...Array.from(event.target.files || [])];
    event.target.value = "";
    if (selected.length > 3 || selected.reduce((total, file) => total + file.size, 0) > 5 * 1024 * 1024) {
      setFileError("Choose up to 3 files, totaling no more than 5 MB.");
      return;
    }
    if (selected.some((file) => !/\.(pdf|docx|txt|png|jpe?g)$/i.test(file.name) || !file.size)) {
      setFileError("Choose non-empty PDF, DOCX, TXT, PNG, or JPG files.");
      return;
    }
    setFiles(selected);
    setFileError("");
  };

  useEffect(() => {
    if (!prefill) return;
    setForm((current) => ({ ...current, ...prefill, requestType: "contact" }));
    setStatus("idle");
    setFeedback("");
  }, [prefill]);

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "sending") return;
    if (fileError) { fileInput.current?.focus(); return; }
    setStatus("sending");
    setFeedback("");

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 60000);

    try {
      const attachments = await Promise.all(files.map(encodeFile));
      const response = await fetch(`/api/${form.requestType === "demo" ? "demo" : "contact"}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ ...form, attachments, source: window.location.pathname }),
        signal: controller.signal,
      });
      const result = (await response.json().catch(() => ({}))) as { message?: string };
      if (!response.ok) throw new Error(result.message || "We could not send your request.");

      setStatus("sent");
      setFeedback(result.message || "Thanks—your note is on its way to our team.");
      setForm((current) => ({ ...initialForm, requestType: current.requestType, projectType: current.projectType }));
      setFiles([]);
      setFileError("");
    } catch (error) {
      const message = error instanceof Error && error.name === "AbortError"
        ? "The request timed out. Please try again."
        : error instanceof Error
          ? error.message
          : "We could not send your request. Please try again.";
      setStatus("error");
      setFeedback(message);
    } finally {
      window.clearTimeout(timeout);
    }
  };

  return (
    <section className="contact-section" id="contact">
      <div className="contact-orb contact-orb-one" aria-hidden="true" />
      <div className="contact-orb contact-orb-two" aria-hidden="true" />
      <div className="site-container contact-grid">
        <div className="contact-copy">
          <span className="contact-signal" aria-hidden="true">LET’S TALK ABOUT WHAT’S NEXT</span>
          <p className="eyebrow">{servicePrompt ? "Your à la carte request" : "Start a conversation"}</p>
          <h2>{servicePrompt ? <>Tell us what <em>you need.</em></> : <>Good things start <em>with a conversation.</em></>}</h2>
          <p>
            Share the goal, the friction, or even the half-formed idea. We’ll
            help you identify the smartest next step—whether that is a focused
            prototype, a new product, or a better internal system.
          </p>
          <div className="contact-points">
            <span><Icon name="check" /> Clear scope before commitment</span>
            <span><Icon name="check" /> A direct conversation with a builder</span>
            <span><Icon name="check" /> No automated sales sequence</span>
          </div>
        </div>

        <form className="contact-form" onSubmit={handleSubmit}>
          <fieldset className="contact-fields" disabled={status === "sending"}>
          {servicePrompt && <p className="service-request-summary">{form.projectType} · No retainer required</p>}
          <div className="request-toggle" aria-label="Type of request">
            <button
              type="button"
              className={form.requestType === "contact" ? "is-active" : ""}
              aria-pressed={form.requestType === "contact"}
              onClick={() => setForm((current) => ({ ...current, requestType: "contact" }))}
            >
              {servicePrompt ? "Request service" : "Request a quote"}
            </button>
            <button
              type="button"
              className={form.requestType === "demo" ? "is-active" : ""}
              aria-pressed={form.requestType === "demo"}
              onClick={() => setForm((current) => ({ ...current, requestType: "demo" }))}
            >
              Request a demo
            </button>
          </div>

          <div className="form-grid">
            <label className="field">
              <span>Name</span>
              <input id="contact-name" name="name" value={form.name} onChange={handleChange} autoComplete="name" required maxLength={120} placeholder="Your name" />
            </label>
            <label className="field">
              <span>Work email</span>
              <input name="email" value={form.email} onChange={handleChange} type="email" autoComplete="email" required maxLength={180} placeholder="you@company.com" />
            </label>
            <label className="field">
              <span>Phone <small>Optional</small></span>
              <input name="phone" value={form.phone} onChange={handleChange} type="tel" autoComplete="tel" maxLength={50} placeholder="Your phone number" />
            </label>
            <label className="field">
              <span>Company <small>Optional</small></span>
              <input name="company" value={form.company} onChange={handleChange} autoComplete="organization" maxLength={140} placeholder="Company name" />
            </label>
            <label className="field field-full">
              <span>What can we help with?</span>
              <select name="projectType" value={form.projectType} onChange={handleChange} required>
                <option value="" disabled>Select a service</option>
                <optgroup label="À la carte services">
                  {Object.keys(servicePrompts).map((service) => <option key={service}>{service}</option>)}
                </optgroup>
                <option>Website or web platform</option>
                <option>Mobile app</option>
                <option>Custom business software</option>
                <option>AI and automation</option>
                <option>Cloud and integrations</option>
                <option>Not sure yet</option>
              </select>
            </label>
            <label className="field field-full">
              <span>Estimated investment <small>Optional</small></span>
              <select name="budget" value={form.budget} onChange={handleChange}>
                <option value="">Help me scope it</option>
                <option>Under $10,000</option>
                <option>$10,000–$25,000</option>
                <option>$25,000–$75,000</option>
                <option>$75,000+</option>
              </select>
            </label>
            <label className="field field-full">
              <span>{servicePrompt ? "Briefly describe what you need" : "Project details"}</span>
              {servicePrompt && <small>{servicePrompt}</small>}
              <textarea name="message" value={form.message} onChange={handleChange} required minLength={20} maxLength={4000} rows={5} placeholder={servicePrompt || "What are you building, who is it for, and what would success look like?"} />
            </label>
            <div className="field field-full attachment-field">
              <label htmlFor="request-files">Supporting documents <small>Optional</small></label>
              <span id="attachment-help">PDF, DOCX, TXT, PNG, or JPG. Up to 3 files, 5 MB total. Files are emailed to our team with your request.</span>
              <input ref={fileInput} id="request-files" type="file" multiple accept=".pdf,.docx,.txt,.png,.jpg,.jpeg" onChange={handleFiles} aria-describedby="attachment-help attachment-error" />
              {files.length > 0 && <ul className="attachment-list">{files.map((file, index) => <li key={`${file.name}-${index}`}><span>{file.name} <small>({Math.ceil(file.size / 1024)} KB)</small></span><button type="button" aria-label={`Remove ${file.name}`} onClick={() => { setFiles((current) => current.filter((_, i) => i !== index)); setFileError(""); }}>Remove</button></li>)}</ul>}
              <span id="attachment-error" role="alert">{fileError}</span>
            </div>
            <label className="honeypot" aria-hidden="true">
              Website
              <input name="website" value={form.website} onChange={handleChange} tabIndex={-1} autoComplete="off" />
            </label>
          </div>

          <div className="form-footer">
            <p>By sending, you agree that we may reply about your request.</p>
            <button className="button" type="submit" disabled={status === "sending"}>
              {status === "sending" ? "Sending…" : "Send request"}
              {status !== "sending" && <Icon name="send" />}
            </button>
          </div>
          </fieldset>
          <div className={`form-feedback${status === "sent" ? " is-success" : status === "error" ? " is-error" : ""}`} role="status" aria-live="polite">
            {feedback}
          </div>
        </form>
      </div>
    </section>
  );
};

export default ContactSection;
